import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type EasingFn = (t: number) => number;

export interface SmoothScrollOptions {
  duration?: number;
  offset?: number;
  easing?: EasingFn;
  block?: ScrollLogicalPosition;
}

@Injectable({
  providedIn: 'root',
})
export class SmoothScrollService {
  private readonly defaultDuration = 700;
  private readonly defaultOffset = 0;
  // Более мягкая кривая анимации
  private readonly defaultEasing: EasingFn = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private get win(): (Window & typeof globalThis) | undefined {
    return this.isBrowser ? window : undefined;
  }

  private raf(cb: FrameRequestCallback): number {
    if (this.isBrowser && this.win?.requestAnimationFrame) {
      return this.win.requestAnimationFrame(cb);
    }
    return setTimeout(() => cb(Date.now()), 16) as unknown as number;
  }

  async scrollTo(target: string | HTMLElement, options: SmoothScrollOptions = {}): Promise<void> {
    const duration = Math.max(0, options.duration ?? this.defaultDuration);
    const offset = options.offset ?? this.defaultOffset;
    const easing = options.easing ?? this.defaultEasing;
    const block = options.block ?? 'start';

    const doc = this.win?.document;
    const el: HTMLElement | null =
      typeof target === 'string' ? (doc?.querySelector(target) as HTMLElement | null) : target;

    if (!el) return;

    const startY = this.win?.scrollY ?? this.win?.pageYOffset ?? 0;

    await new Promise<void>((resolve) => {
      let startTime = 0;

      const frame = (timestamp: number) => {
        if (!startTime) startTime = timestamp;

        const rawProgress = (timestamp - startTime) / duration;
        const t = Math.min(1, rawProgress);
        const eased = easing(t);

        // ДИНАМИЧЕСКИЙ ТРЕКИНГ: каждый кадр проверяем, не сдвинул ли виджет страницу
        const currentRect = el.getBoundingClientRect();
        const currentScrollY = this.win?.scrollY ?? this.win?.pageYOffset ?? 0;

        let targetAbsoluteY = currentScrollY + currentRect.top;
        if (block === 'center') {
          const vh = this.win?.innerHeight ?? 0;
          targetAbsoluteY = currentScrollY + currentRect.top - (vh / 2 - currentRect.height / 2);
        } else if (block === 'end') {
          const vh = this.win?.innerHeight ?? 0;
          targetAbsoluteY = currentScrollY + currentRect.bottom - vh;
        }

        const finalY = targetAbsoluteY - offset;

        // Если приехали или время вышло — стоп
        if (t >= 1 || Math.abs(finalY - startY) < 1 || duration === 0) {
          this.win?.scrollTo?.(0, finalY);
          resolve();
          return;
        }

        // Корректируем полет на лету
        const currentY = startY + (finalY - startY) * eased;
        this.win?.scrollTo?.(0, currentY);

        this.raf(frame);
      };

      this.raf(frame);
    });
  }

  async scrollToHash(hash: string, options?: SmoothScrollOptions): Promise<void> {
    const normalized = hash.startsWith('#') ? hash : `#${hash}`;
    await this.scrollTo(normalized, options);
  }
}